import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import MockupsPreview from "@/pages/MockupsPreview";
import AgendaWidgetMockup from "@/pages/mockups/AgendaWidgetMockup";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import StaffSetPasswordPage from "@/pages/auth/StaffSetPasswordPage";
import VerifyPage from "@/pages/auth/VerifyPage";
import SetupWizard from "@/pages/setup/SetupWizard";
import Dashboard from "@/pages/dashboard/Dashboard";
import ServicesPage from "@/pages/dashboard/ServicesPage";
import StaffPage from "@/pages/dashboard/StaffPage";
import AppointmentsPage from "@/pages/dashboard/AppointmentsPage";
import CalendarPage from "@/pages/dashboard/CalendarPage";
import CustomersPage from "@/pages/dashboard/CustomersPage";
import CustomerDetailPage from "@/pages/dashboard/CustomerDetailPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import StaffDashboard from "@/pages/staff/StaffDashboard";
import PublicBookingPage from "@/pages/booking/PublicBookingPage";
import ManageAppointmentPage from "@/pages/booking/ManageAppointmentPage";
import PaymentsPage from "@/pages/dashboard/PaymentsPage";
import PricingPage from "@/pages/dashboard/PricingPage";
import BalancePaymentPage from "@/pages/payment/BalancePaymentPage";
// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBusinessDetail from "@/pages/admin/AdminBusinessDetail";
import AdminRegistrationRequests from "@/pages/admin/AdminRegistrationRequests";
import AdminPlansPage from "@/pages/admin/AdminPlansPage";
import ScrollToTop from "@/components/ScrollToTop";
import OpenInBrowserBanner from "@/components/OpenInBrowserBanner";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Route - redirects based on user role
const DashboardRoute = () => {
  const { user, business, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  // If owner without business, go to setup
  if (user.role === "owner" && !business) {
    return <Navigate to="/setup" replace />;
  }

  // If staff, show staff dashboard
  if (user.role === "staff") {
    return <StaffDashboard />;
  }

  // Default to owner dashboard
  return <Dashboard />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/mockups" element={<MockupsPreview />} />
      <Route path="/mockups/agenda-widget" element={<AgendaWidgetMockup />} />
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/staff/set-password" element={<StaffSetPasswordPage />} />

      {/* Public Booking */}
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/appointment/:token" element={<ManageAppointmentPage />} />
      <Route path="/pay/:appointmentId" element={<BalancePaymentPage />} />

      {/* Setup Wizard */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute>
            <SetupWizard />
          </ProtectedRoute>
        }
      />

      {/* Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["owner", "staff"]}>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/calendar"
        element={
          <ProtectedRoute allowedRoles={["owner", "staff"]}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/services"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/staff"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <StaffPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/appointments"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <AppointmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/customers"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/customers/:customerId"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <CustomerDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/payments"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <PaymentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/pricing"
        element={
          <ProtectedRoute allowedRoles={["owner"]}>
            <PricingPage />
          </ProtectedRoute>
        }
      />

      {/* Staff Dashboard - redirect to /dashboard for staff */}
      <Route
        path="/staff"
        element={<Navigate to="/dashboard" replace />}
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route
        path="/admin/businesses/:businessId"
        element={<AdminBusinessDetail />}
      />
      <Route
        path="/admin/registration-requests"
        element={<AdminRegistrationRequests />}
      />
      <Route
        path="/admin/plans"
        element={<AdminPlansPage />}
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OpenInBrowserBanner />
        <ScrollToTop />
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
